/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/jsx-props-no-spreading */
import * as React from "react";
import * as _ from "underscore";
import SearchInput, { createFilter } from "react-search-input";
import browserHistory from "web_modules/redux/history";
import { RouteComponentProps } from "react-router";
import { parse } from "query-string";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import moment from "moment-timezone";
import OldIcon from "@eave/shared/components/OldIcon";
import { DocTypes, DocShape } from "@eave/shared/types/api";
import { Button, Dropdown, Modal, Select, SelectItem, Toggle } from "carbon-components-react";
import {
  Upload20,
  DocumentSubtract20,
  DocumentView20,
  Launch24,
  Edit24,
  Download24,
} from "@carbon/icons-react";

import { getFileDownloadBaseURL } from "config";
import DocumentLink from "underwriter_dashboard/DocumentViewer/DocumentLink";
import {
  docToHumanStr,
  docTypeToCategory,
  docTypeToHumanStr,
  docTypeSelectOpts,
} from "underwriter_dashboard/shared";
import DocUploader from "underwriter_dashboard/shared/DocUploader";
import DocChanger from "underwriter_dashboard/shared/DocChanger";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import {
  documentOwnerOptions,
  initiateBlobDownload,
  cleanFileName,
} from "underwriter_dashboard/shared/Documents/helpers";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import DocEntry from "underwriter_dashboard/shared/DocEntry";
import { loadApp } from "underwriter_dashboard/actions";
import {
  DocumentFieldUpdate,
  fetchDocData,
  updateDocumentRequest,
  updateDocumentVisibility,
} from "underwriter_dashboard/shared/Documents/api-helpers";
import DoubleClickToEdit from "web_modules/shared/DoubleClickToEdit";
import APIClient from "web_modules/APIClient";
import Sortable from "web_modules/shared/sortable_multi";

import ColumnPicker from "./ColumnPicker";
import PurchaseContract from "./PurchaseContract";

const classnames = require("classnames");
const styles = require("./styles.module.scss");

/* helpers */

const updatedDate = doc => {
  let mostRecentUpdate = moment(doc.updated_at);

  if (doc.sections && doc.sections.length) {
    for (let i = 0; i < doc.sections.length; i += 1) {
      for (let j = 0; j < doc.sections[i].fields.length; j += 1) {
        const fieldUpdatedDate = moment(doc.sections[i].fields[j].updated_at);
        if (fieldUpdatedDate.isAfter(mostRecentUpdate)) {
          mostRecentUpdate = fieldUpdatedDate;
        }
      }
    }
  }
  return mostRecentUpdate;
};

interface HurdleEditorProps extends HurdleProps {
  dispatch: Function;
}

interface DocInfoMapEntry {
  headerDisplayText: string;
  rowDisplayText: Function;
  sortAndSearchValue?: Function;
  tooltipText?: Function;
}

interface DocumentInformationMap {
  id: DocInfoMapEntry;
  category: DocInfoMapEntry;
  used: DocInfoMapEntry;
  type: DocInfoMapEntry;
  created_at: DocInfoMapEntry;
  updated_at: DocInfoMapEntry;
  filename: DocInfoMapEntry;
  original_filename: DocInfoMapEntry;
  owner_entity: DocInfoMapEntry;
  visibility: DocInfoMapEntry;
  source: DocInfoMapEntry;
}

const docInfoMap = (app, handleSave): DocumentInformationMap => {
  const owners = documentOwnerOptions(app);

  const ownerDisplayText = doc => {
    const ownerEntity = owners.filter(
      option =>
        option.value.owner_entity_type === doc.owner_entity_type &&
        option.value.owner_entity_id === doc.owner_entity_id,
    );
    if (!ownerEntity || !ownerEntity.length) {
      return "";
    }
    return ownerEntity[0].label;
  };

  return {
    id: {
      headerDisplayText: "ID",
      rowDisplayText: doc => doc.id,
    },
    category: {
      headerDisplayText: "Category",
      rowDisplayText: doc => docTypeToCategory(doc.type),
    },
    used: {
      headerDisplayText: "Used?",
      rowDisplayText: doc => (doc.used ? "Yes" : "No"),
    },
    type: {
      headerDisplayText: "Type",
      sortAndSearchValue: doc => docTypeToHumanStr(doc.type),
      rowDisplayText: doc => (
        <Select
          id="select-document-type"
          style={{ width: "100%" }}
          inline
          labelText=""
          placeholder="Select type"
          value={doc.type}
          onChange={e =>
            handleSave({
              ...doc,
              type: e.target.value,
            })
          }
        >
          {docTypeSelectOpts.map(opt => (
            <SelectItem key={opt.value} label={opt.label} value={opt.value} />
          ))}
        </Select>
      ),
    },
    created_at: {
      headerDisplayText: "Date received",
      sortAndSearchValue: doc => moment(doc.created_at),
      rowDisplayText: doc => moment(doc.created_at).tz(moment.tz.guess()).format("M/D/YY, H:mm z"),
      tooltipText: doc =>
        moment(doc.created_at).tz(moment.tz.guess()).format("dddd MMMM D, YYYY | h:mm a z"),
    },
    updated_at: {
      headerDisplayText: "Date updated",
      sortAndSearchValue: updatedDate,
      rowDisplayText: doc => updatedDate(doc).tz(moment.tz.guess()).format("M/D/YY, H:mm z"),
      tooltipText: doc =>
        updatedDate(doc).tz(moment.tz.guess()).format("dddd MMMM D, YYYY | h:mm a z"),
    },
    filename: {
      headerDisplayText: "Filename",
      sortAndSearchValue: doc => doc.filename,
      rowDisplayText: doc => (
        <DoubleClickToEdit
          acceptedInput="text"
          value={doc.filename}
          onSave={value =>
            handleSave({
              ...doc,
              revised_filename: cleanFileName(value),
            })
          }
        />
      ),
    },
    original_filename: {
      headerDisplayText: "Original filename",
      rowDisplayText: doc => doc.original_filename,
    },
    owner_entity: {
      headerDisplayText: "Owner entity",
      sortAndSearchValue: doc => ownerDisplayText(doc),
      rowDisplayText: (doc: DocShape) => {
        return (
          <div>
            <Select
              hideLabel
              id={`${doc.id}-owner-select`}
              size="sm"
              value={doc.owner_entity_id || "-"}
              onChange={e => {
                const selectedOwnerID = e.target.value;
                if (selectedOwnerID === "") {
                  handleSave({
                    ...doc,
                    owner_entity_id: null,
                    owner_entity_type: null,
                  });
                  return;
                }

                // If a non-nil value was selected, find the owner entity and update the corresponding fields
                const ownerEntityValues = owners.find(
                  o => o.value.owner_entity_id === parseInt(selectedOwnerID, 10),
                ).value;

                const { owner_entity_id, owner_entity_type } = ownerEntityValues;
                handleSave({
                  ...doc,
                  owner_entity_id,
                  owner_entity_type,
                });
              }}
            >
              <SelectItem label="-" value="" />
              {owners.map(opt => (
                <SelectItem
                  key={`${doc.id}-${opt.value.owner_entity_id}-${opt.value.owner_entity_type}`}
                  label={opt.label}
                  value={opt.value.owner_entity_id}
                />
              ))}
            </Select>
          </div>
        );
      },
    },
    visibility: {
      headerDisplayText: "Borrower visible?",
      rowDisplayText: doc => (doc.visible ? "Yes" : "No"),
    },
    source: {
      headerDisplayText: "Source",
      rowDisplayText: doc => doc.source,
    },
  };
};

interface HurdleEditorWithRouteProps extends HurdleEditorProps, RouteComponentProps<any> {}

class Editor extends React.Component<HurdleEditorWithRouteProps, any> {
  private sorter: Sortable;

  private readonly docInfoMap: DocumentInformationMap;

  private readonly allColumns;

  private readonly defaultColumns;

  constructor(props) {
    super(props);
    const allDocs = _.compact([...(props.app.all_documents || [])]);
    this.sorter = new Sortable(allDocs);
    this.defaultColumns = [
      "used",
      "id",
      "category",
      "type",
      "filename",
      "created_at",
      "owner_entity",
    ];

    this.state = {
      docs: this.sorter.sortables(),
      openDoc: null,
      prevDocs: [],
      shownColumns: [...this.defaultColumns],
      checkedDocs: [],
      searchTerm: "",
      hideUnused: true,
    };

    this.docInfoMap = docInfoMap(props.app, this.onSave);
    this.allColumns = Object.keys(this.docInfoMap);
  }

  getUrlDocId(props) {
    const { docs } = this.state;
    const docId = parseInt(parse(props.location.search).d, 10);
    return docs.map(d => d.id).includes(docId) ? docId : null;
  }

  componentDidMount() {
    const docId = this.getUrlDocId(this.props);
    if (docId) {
      this.openDoc(docId);
    } else {
      this.closeDoc();
    }
    this.loadAndSetSavedSettings();
  }

  searchUpdated = term => {
    this.setState({ searchTerm: term });
  };

  clearSortKeys() {
    const sortKeys = this.sorter.getSortKeys();
    sortKeys.map(item => this.sorter.clearSortKey(item.key));
  }

  resetToDefault() {
    this.clearSortKeys();
    this.setState(
      {
        shownColumns: this.defaultColumns,
      },
      this.setSavedSettings,
    );
  }

  loadAndSetSavedSettings() {
    this.clearSortKeys();
    const savedSettingsString = localStorage.getItem("documentHurdleSettings");
    if (savedSettingsString !== null) {
      const savedSettings = JSON.parse(savedSettingsString);
      this.setState({
        shownColumns: savedSettings.shownColumns,
      });

      for (let i = savedSettings.sortKeys.length; i > 0; i -= 1) {
        const sortKey = savedSettings.sortKeys[i - 1];
        this.setSortKey(sortKey.key);
        if (sortKey.order === "desc") {
          this.setSortKey(sortKey.key);
        }
      }
    }
  }

  setSavedSettings() {
    const sortKeys = this.sorter.getSortKeys();
    const { shownColumns } = this.state;

    const settings = {
      sortKeys,
      shownColumns,
    };

    localStorage.setItem("documentHurdleSettings", JSON.stringify(settings));
  }

  componentDidUpdate(prevProps) {
    const { app } = this.props;
    const prevDocId = this.getUrlDocId(prevProps);
    const docId = this.getUrlDocId(this.props);
    if (prevDocId !== docId) {
      this.openDoc(docId);
    }

    if (!_.isEqual(prevProps, this.props)) {
      const allDocs = _.compact([...((app.all_documents as any) || [])]);
      this.sorter.updateCollection(allDocs);
      this.setState({
        docs: this.sorter.sortables(),
      });
    }
  }

  setSortKey(sortKey) {
    this.sorter.setSortKey(
      sortKey,
      this.docInfoMap[sortKey].sortAndSearchValue || this.docInfoMap[sortKey].rowDisplayText,
    );
    this.setState({ docs: this.sorter.sortables() });
  }

  clearSortKey(sortKey) {
    this.sorter.clearSortKey(sortKey);
    this.setState({ docs: this.sorter.sortables() });
  }

  sortableTableHeaderItem(sortKey) {
    const fieldKeyToDisplay = key =>
      this.docInfoMap[key] ? this.docInfoMap[key].headerDisplayText : "uh oh";

    // Some columns require a min-width to ensure they don't get clipped
    const wideColumns = ["type", "filename"];
    const sortKeyItem = this.sorter.getSortKeys().filter(item => item.key === sortKey);
    const sortDirection = sortKeyItem.length && sortKeyItem[0].order;

    return (
      <th key={sortKey} style={wideColumns.includes(sortKey) ? { minWidth: "145px" } : {}}>
        <span
          className={styles.clickable}
          onClick={() => {
            this.setSortKey(sortKey);
          }}
        >
          {fieldKeyToDisplay(sortKey)}
          {sortDirection === "desc" && (
            <i className={classnames("fa", "fa-chevron-up", styles.icon)} />
          )}
          {sortDirection === "asc" && (
            <i className={classnames("fa", "fa-chevron-down", styles.icon)} />
          )}
        </span>
        <span className={styles.clickable}>
          {sortDirection ? (
            <i
              onClick={() => {
                this.clearSortKey(sortKey);
              }}
              className={classnames("fa", "fa-close", styles.icon)}
            />
          ) : (
            false
          )}
        </span>
      </th>
    );
  }

  renderDocEntryModal(docID) {
    const { app, hurdle, dispatch } = this.props;

    const allDocs = _.compact([...((app.all_documents as any) || [])]);
    const doc = allDocs.filter(d => d.id === docID);

    const appWithAllDocs = {
      ...app,
      documents: allDocs,
    };

    let title = "Review Document";
    if (doc.length) {
      const d = doc[0];
      title = `${docTypeToHumanStr(d.type)} ${docToHumanStr(d)}`;
    }

    if (doc[0].type === DocTypes.PurchaseContract) {
      return (
        <PurchaseContract
          app={app}
          dispatch={dispatch}
          hurdle={hurdle}
          onHide={() => {
            this.closeDoc();
          }}
        />
      );
    }

    return (
      <DocEntry
        key={docID}
        app={appWithAllDocs}
        docNotFoundMsg=""
        hurdle={hurdle}
        modalTitle={title}
        docId={docID}
        onHide={() => {
          this.closeDoc();
        }}
      />
    );
  }

  onSave = (doc, documentFieldsDiff: DocumentFieldUpdate[]) => {
    const { app, dispatch } = this.props;

    return updateDocumentRequest(doc)
      .then(() => updateDocumentVisibility(doc.id, documentFieldsDiff))
      .then(() => {
        loadApp(dispatch, app.id);
      });
  };

  batchMarkUnused = docs => {
    const { app, dispatch } = this.props;

    // .map() the selected docs to become a list of diff objects
    APIClient.put(`/document_field_list`, {
      document_fields: docs.map(doc => {
        return {
          document_id: doc.id,
          section: "used",
          field_key: "not_used",
          field_value: "true",
        };
      }),
    }).then(() => {
      loadApp(dispatch, app.id);
      this.setState({ showingMarkUnusedModal: false, checkedDocs: [] });
    });
  };

  openDoc(docID) {
    if (docID && !this.getUrlDocId(this.props)) {
      browserHistory.push({ search: `?d=${docID}` });
    }
    this.setState({
      openDoc: docID,
    });
  }

  closeDoc = () => {
    browserHistory.push({ search: "" });
    this.setState(prevState => ({
      openDoc: null,
      prevDocs: [prevState.openDoc],
    }));
  };

  handleShowColumn = column => {
    this.setState(prevState => ({ shownColumns: prevState.shownColumns.concat(column) }));
  };

  handleHideColumn = column => {
    this.clearSortKey(column);
    this.setState(prevState => ({
      shownColumns: prevState.shownColumns.filter(col => col !== column),
    }));
  };

  hideModalAndRefresh() {
    const { app, dispatch } = this.props;
    loadApp(dispatch, app.id);
    this.setState({
      showingUploader: false,
      showingChanger: false,
      showingMarkUnusedModal: false,
    });
  }

  docTransformer = doc => {
    const keys = Object.keys(this.docInfoMap);
    const d = {};
    for (let i = 0; i < keys.length; i += 1) {
      const f =
        this.docInfoMap[keys[i]].sortAndSearchValue || this.docInfoMap[keys[i]].rowDisplayText;
      d[keys[i]] = f(doc);
    }
    return d;
  };

  handleCheck = (e, docId) => {
    if (e.target.checked) {
      this.setState(prevState => ({
        checkedDocs: prevState.checkedDocs.concat(docId),
      }));
    } else {
      this.setState(prevState => ({
        checkedDocs: prevState.checkedDocs.filter(id => docId !== id),
      }));
    }
  };

  openComboPDF = s3_key => {
    const { app } = this.props;

    const uuid = s3_key.match(/[\w-]+?(?=\.pdf)/)[0];
    window.open(`/underwriter/app/${app.id}/documents/viewer/${uuid}`, "_blank");

    // reset after opening
    this.setState({ mergedFileS3Key: "", mergedFileSkippedFiles: [] });
  };

  downloadPackageFile = (s3_key, filename) => {
    window.location.href = getFileDownloadBaseURL(s3_key, filename);

    // reset after downloading
    this.setState({ mergedFileFilename: "", mergedFileS3Key: "", mergedFileSkippedFiles: [] });
  };

  renderMergedFileModal() {
    const { mergedFileFilename, mergedFileS3Key, mergedFileSkippedFiles } = this.state;

    return (
      <Modal
        open
        passiveModal
        modalHeading="Unmergeable documents"
        onRequestClose={() => {
          if (mergedFileFilename) {
            this.downloadPackageFile(mergedFileS3Key, mergedFileFilename);
          } else {
            this.openComboPDF(mergedFileS3Key);
          }
        }}
      >
        <p>
          The following documents are not included in the combined file. They must be fetched
          separately:
        </p>
        <div className={styles.mergedFile}>
          <ul>
            {mergedFileSkippedFiles.map((filename, index) => (
              <li key={index}>{filename}</li>
            ))}
          </ul>
        </div>
      </Modal>
    );
  }

  handleMergeDocs = () => {
    const { checkedDocs } = this.state;
    const { app } = this.props;

    APIClient.post(`/uw/${app.id}/documents_to_pdf`, {
      document_ids: checkedDocs,
    })
      .then(({ s3_key, unmerged_files }) => {
        if (unmerged_files.length > 0) {
          this.setState({
            mergedFileS3Key: s3_key,
            mergedFileSkippedFiles: unmerged_files,
          });
        } else {
          this.openComboPDF(s3_key);
        }
      })
      .catch(({ meta: { error_detail } }) =>
        this.setState({
          errorHeading: "We couldn't merge the documents",
          errorContent: `Please ensure that all documents are a supported file format or notify #hlhl-engineering with the following error: ${error_detail}`,
        }),
      );
  };

  handleDownloadPackages = ({ package_name, as_pdf }) => {
    const { app } = this.props;

    let url = `/uw/${app.id}/download_documents?package_name=${package_name}`;
    if (as_pdf) {
      url = `${url}&pdf=true`;
    }

    APIClient.get(url)
      .then(({ filename, s3_key, unmerged_files }) => {
        if (unmerged_files.length > 0) {
          this.setState({
            mergedFileFilename: filename,
            mergedFileS3Key: s3_key,
            mergedFileSkippedFiles: unmerged_files,
          });
        } else {
          this.downloadPackageFile(s3_key, filename);
        }
      })
      .catch(() =>
        this.setState({
          errorHeading: "Error downloading the documents package",
          errorContent: "Please try again or notify #hlhl-engineering",
        }),
      );
  };

  renderErrorModal() {
    const { errorHeading, errorContent } = this.state;

    return (
      <Modal
        open
        passiveModal
        modalHeading={errorHeading}
        onRequestClose={() => this.setState({ errorHeading: "", errorContent: "" })}
      >
        {errorContent}
      </Modal>
    );
  }

  render() {
    const { app, hurdle, onHide } = this.props;
    const {
      docs,
      checkedDocs,
      openDoc,
      prevDocs,
      hideUnused,
      searchTerm,
      shownColumns,
      showingChanger,
      showingMarkUnusedModal,
      showingUploader,
      mergedFileSkippedFiles,
      errorHeading,
      errorContent,
    } = this.state;

    const KEYS_TO_FILTERS = [...this.allColumns];

    const docIDs = docs
      .map(doc => this.docTransformer(doc))
      .filter(createFilter(searchTerm.replace(/[^0-9a-z:]/gi, " "), KEYS_TO_FILTERS))
      .map(doc => doc.id);

    let filteredDocs = docs.filter(d => docIDs.indexOf(d.id) >= 0);
    if (hideUnused) {
      filteredDocs = filteredDocs.filter(doc => doc.used === hideUnused);
    }

    if (openDoc) {
      return (
        <HurdleView app={app} hurdle={hurdle} onHide={this.closeDoc}>
          {this.renderDocEntryModal(openDoc)}
        </HurdleView>
      );
    }

    return (
      <HurdleView
        app={app}
        hurdle={hurdle}
        onHide={onHide}
        className={styles.documentsContainer}
        contentClassName={styles.documentsContent}
      >
        <Modal
          open={showingMarkUnusedModal}
          modalHeading="Mark selected docs as unused?"
          primaryButtonText="Mark Unused"
          secondaryButtonText="Cancel"
          onBlur={() => {
            this.setState({ showingMarkUnusedModal: false });
          }}
          onRequestClose={() => {
            this.setState({ showingMarkUnusedModal: false });
          }}
          onRequestSubmit={() => {
            this.batchMarkUnused(docs.filter(doc => checkedDocs.indexOf(doc.id) > -1));
          }}
        >
          The following docs are selected :{" "}
          <ul>
            {docs
              .filter(doc => checkedDocs.indexOf(doc.id) > -1)
              .map(c => (
                <li key={c.id} style={{ paddingLeft: "1rem", fontWeight: "bold" }}>
                  - {c.filename}
                </li>
              ))}
          </ul>
        </Modal>
        {showingUploader && (
          <DocUploader
            app={app}
            onHide={() => this.setState({ showingUploader: false })}
            onSave={() => this.hideModalAndRefresh()}
          />
        )}

        {errorHeading && errorContent && this.renderErrorModal()}

        {showingChanger && (
          <DocChanger
            app={app}
            onHide={() =>
              this.setState(prevState => ({
                ...prevState,
                showingChanger: false,
                checkedDocs: [],
                prevDocs: prevState.checkedDocs,
              }))
            }
            onSave={(doc: DocShape, diff) => this.onSave(doc, diff)}
            documents={docs.filter(doc => checkedDocs.indexOf(doc.id) > -1)}
            ownerOptions={documentOwnerOptions(app)}
          />
        )}

        {mergedFileSkippedFiles &&
          mergedFileSkippedFiles.length > 0 &&
          this.renderMergedFileModal()}
        <div className={styles.primaryActions}>
          <Button
            onClick={() => this.setState({ showingUploader: true })}
            kind="tertiary"
            size="small"
            renderIcon={Upload20}
            iconDescription="Upload a new document"
            tooltipPosition="bottom"
            tooltipAlignment="center"
            style={{ marginRight: "1rem" }}
          >
            Upload
          </Button>
          <Dropdown
            label="Download package"
            itemToString={item => (item ? item.label : "")}
            items={[
              {
                label: "All Used Docs",
                package_name: "AllDocs",
                as_pdf: false,
              },
              {
                label: "Pre-Funding Investor Package",
                package_name: "PreFunding",
                as_pdf: false,
              },
              {
                label: "Pre-Funding Investor Package (as PDF)",
                package_name: "PreFunding",
                as_pdf: true,
              },
              {
                label: "Post-Closing Investor Package",
                package_name: "PostClosing",
                as_pdf: false,
              },
              {
                label: "Post-Closing Investor Package (as PDF)",
                package_name: "PostClosing",
                as_pdf: true,
              },
            ]}
            onChange={(e: any) => {
              const { package_name, as_pdf } = e.selectedItem;
              this.handleDownloadPackages({ package_name, as_pdf });
            }}
            id="document-download-package"
            size="sm"
            style={{ minWidth: "330px" }}
            light
          />
        </div>

        <div className={styles.buttonBar}>
          <span className={styles.searchContainer}>
            <SearchInput
              className={styles["search-input"]}
              onChange={this.searchUpdated}
              fuzzy
              caseSensitive={false}
              sortResults
              value={searchTerm}
              placeholder="Search"
            />
            <OldIcon
              type="close"
              size={24}
              className={styles.closeIcon}
              onClick={() => this.searchUpdated("")}
            />
            <OldIcon type="search" size={24} className={styles.searchIcon} />
          </span>

          <span className={styles.buttonBarContent}>
            <Button
              disabled={!checkedDocs.length}
              onClick={() => this.setState({ showingChanger: true })}
              renderIcon={Edit24}
              kind="ghost"
              size="small"
            >
              Edit Documents
            </Button>
            <Button
              disabled={!checkedDocs.length}
              onClick={() => this.setState({ showingMarkUnusedModal: true })}
              renderIcon={DocumentSubtract20}
              kind="ghost"
              size="small"
            >
              Mark unused
            </Button>
            <Button
              disabled={!checkedDocs.length}
              onClick={this.handleMergeDocs}
              renderIcon={DocumentView20}
              kind="ghost"
              size="small"
            >
              View selected docs
            </Button>

            <Toggle
              id="toggle-unused-document-visibility"
              className={styles.toggle}
              toggled={hideUnused}
              onToggle={() => this.setState({ hideUnused: !hideUnused })}
              labelA="Show unused"
              labelB="Hide unused"
              labelText=""
            />
          </span>
        </div>

        <table className={styles.table}>
          <thead>
            <tr>
              <th colSpan={3}>View</th>
              {shownColumns.map(key => this.sortableTableHeaderItem(key))}
              <th>
                <ColumnPicker
                  shownColumns={shownColumns}
                  hiddenColumns={this.allColumns.filter(col => shownColumns.indexOf(col) < 0)}
                  showColumn={this.handleShowColumn}
                  hideColumn={this.handleHideColumn}
                  saveSettings={() => this.setSavedSettings()}
                  clearSettings={() => this.resetToDefault()}
                  resetSettings={() => this.loadAndSetSavedSettings()}
                  displayText={column => this.docInfoMap[column].headerDisplayText}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredDocs.map((doc, i) => {
              const sharedButtonProps = {
                hasIconOnly: true,
                tooltipPosition: "right",
                tooltipAlignment: "center",
                kind: "ghost",
                size: "sm",
              };

              return (
                <tr
                  key={doc.id}
                  className={classnames({
                    [styles.lastOpen]: prevDocs.indexOf(doc.id) >= 0,
                    [styles.unused]: !doc.used,
                  })}
                >
                  <td className={styles.rowIndicator}>{i + 1}</td>
                  <td
                    className={classnames({
                      [styles.centered]: true,
                      [styles.checkBox]: true,
                      [styles.showChecked]: checkedDocs.indexOf(doc.id) > -1,
                    })}
                  >
                    <label htmlFor={`check-${doc.id}`} className={styles.inputContainer}>
                      <input
                        id={`check-${doc.id}`}
                        type="checkbox"
                        checked={checkedDocs.indexOf(doc.id) > -1}
                        onChange={e => {
                          this.handleCheck(e, doc.id);
                        }}
                      />
                      <span className={styles.checkMark} />
                    </label>
                  </td>
                  <td style={{ verticalAlign: "middle", width: "115px" }}>
                    <DocumentLink
                      onClick={() => {
                        this.setState(prevState => ({
                          ...prevState,
                          prevDocs: [...prevState.prevDocs, doc.id],
                        }));
                      }}
                      appId={app.id}
                      docId={doc.id}
                    >
                      <Button
                        renderIcon={Launch24}
                        iconDescription={`Open ${doc.revised_filename}`}
                        {...sharedButtonProps}
                      />
                    </DocumentLink>

                    <a>
                      <Button
                        renderIcon={Edit24}
                        iconDescription={`Edit ${doc.revised_filename}`}
                        onClick={() => {
                          this.openDoc(doc.id);
                        }}
                        {...sharedButtonProps}
                      />
                    </a>
                    <a>
                      <Button
                        onClick={async () => {
                          const blobURL = await fetchDocData(doc);
                          initiateBlobDownload(
                            blobURL,
                            doc.revised_filename || doc.original_filename,
                          );
                          URL.revokeObjectURL(blobURL);
                        }}
                        renderIcon={Download24}
                        iconDescription={`Download ${doc.revised_filename}`}
                        {...sharedButtonProps}
                      />
                    </a>
                  </td>
                  {shownColumns.map((key, i) => {
                    const docInfo = this.docInfoMap[key];
                    return (
                      <td
                        key={key}
                        className={classnames({
                          [styles[key]]: styles && styles[key],
                          [styles.valueTrue]: docInfo.rowDisplayText(doc) === "Yes",
                          [styles.valueFalse]: docInfo.rowDisplayText(doc) === "No",
                        })}
                        colSpan={i === shownColumns.length - 1 ? 2 : 1}
                        title={docInfo.tooltipText && docInfo.tooltipText(doc)}
                      >
                        {docInfo.rowDisplayText(doc)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </HurdleView>
    );
  }
}

export default connect()(withRouter(Editor) as any);
